import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import api from "../Utility/api";
import { useAuth } from "../Utility/AuthContext";
import StoryCard from "../Components/StoryCard";
import Seo from "../Components/Seo";
const API = process.env.REACT_APP_API_URL || "";
export default function Profile() {
  const { username } = useParams();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const owner = user?.username === username;
  const [stories, setStories] = useState([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setDescription("");
    setStories([]);
    Promise.all([
      api.get(`${API}/api/stories/author/${encodeURIComponent(username)}`),
      api.get(`${API}/api/author/${encodeURIComponent(username)}`),
    ])
      .then(([storyResponse, authorResponse]) => {
        if (active) {
          setStories(storyResponse.data.sort((a, b) => b.id - a.id));
          setDescription(
            authorResponse.data.description ||
              "A storyteller with a world to share.",
          );
        }
      })
      .catch(() => {
        if (active) setError("This author could not be loaded.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [username]);
  const logout = async () => {
    try {
      await api.post(`${API}/api/logout`);
      setUser(null);
      navigate("/");
    } catch {
      setError("Unable to sign out. Please try again.");
    }
  };
  const remove = async (id) => {
    if (
      !window.confirm(
        "Permanently delete this story? Download a copy from the editor first if you want to keep it.",
      )
    )
      return;
    try {
      await api.delete(`${API}/api/stories/${id}`);
      setStories((current) => current.filter((story) => story.id !== id));
      setNotice("Story deleted.");
    } catch {
      setError("Unable to delete this story.");
    }
  };
  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 7 } }}>
      <Seo
        title={`Stories by ${username}`}
        description={
          description ||
          `Read interactive stories by ${username} on Pick Your End.`
        }
        noindex={Boolean(error)}
      />
      <span className="eyebrow">Meet the storyteller</span>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={3}
        mb={4}
      >
        <Stack direction="row" gap={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: 64,
              height: 64,
              fontFamily: "Georgia",
              fontSize: 30,
            }}
          >
            {username?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h2" component="h1">
              {username}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ maxWidth: 600, overflowWrap: "anywhere" }}
            >
              {description}
            </Typography>
          </Box>
        </Stack>
        {owner && (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Button component={Link} to="/changepassword">
              Change password
            </Button>
            <Button variant="outlined" onClick={logout}>
              Sign out
            </Button>
          </Stack>
        )}
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" component="h2">
          {owner ? "Your stories" : "On the bookshelf"}
        </Typography>
        {owner && (
          <Button component={Link} to="/create" variant="contained">
            + New story
          </Button>
        )}
      </Stack>
      {loading ? (
        <Box textAlign="center" p={6}>
          <CircularProgress aria-label="Loading author" />
        </Box>
      ) : stories.length ? (
        <div className="library-grid">
          {stories.map((story, index) => (
            <StoryCard
              key={story.id}
              story={story}
              index={index}
              owner={owner}
              onDelete={remove}
            />
          ))}
        </div>
      ) : (
        !error && (
          <div className="empty-state">
            <Typography variant="h5">
              The first chapter is still to come.
            </Typography>
            {owner && (
              <Button component={Link} to="/create" sx={{ mt: 2 }}>
                Create your first story →
              </Button>
            )}
          </div>
        )
      )}
    </Container>
  );
}
